import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { submitCandidateIntake, uploadCandidateCv } from "api/Candidate";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const BRAND_ORANGE = "#F15E22";
const QUESTIONS = [
  "Wat motiveert jou om te willen werken, los van salaris of zekerheid?",
  "Beschrijf een situatie (werk, school, thuis of anders) waarin je verantwoordelijkheid nam.",
  "Wat doe jij meestal als iets nieuw of spannend voor je is?",
  "Hoe ga je om met fouten of teleurstelling?",
  "Wat vind jij een eigenschap van jezelf waar je trots op bent, en waarom?",
  "Wat vind jij belangrijk in hoe mensen met elkaar omgaan binnen een team?",
  "Wanneer voel jij je op je best en het meest jezelf?",
];

const MAX_CV_SIZE_BYTES = 10 * 1024 * 1024;

const getStatusCode = (errOrRes) =>
  errOrRes?.status ??
  errOrRes?.response?.status ??
  errOrRes?.response?.statusCode ??
  errOrRes?.data?.status ??
  errOrRes?.error?.status;

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const PageShell = ({ children }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f6f7fb",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
};

const GlassCard = ({ children, sx }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

const CandidateIntake = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [personalityType, setPersonalityType] = useState("");
  const [answers, setAnswers] = useState(QUESTIONS.map(() => ""));

  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState("");
  const [cvUploading, setCvUploading] = useState(false);
  const [cvError, setCvError] = useState("");
  const [cvInfo, setCvInfo] = useState("");

  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [error, setError] = useState("");

  const uploadRequestIdRef = useRef(0);

  useEffect(() => {
    if (!token) setError("Ongeldige of ontbrekende intake link.");
  }, [token]);

  const questionnaireText = useMemo(() => {
    return QUESTIONS.map((q, i) => `${q}\n${answers[i]}`).join("\n\n");
  }, [answers]);

  const isPersonalityValid = personalityType.trim().length === 4;
  const areAllAnswersFilled = answers.every((a) => a.trim().length > 0);
  const isCvValid = Boolean(cvUrl);

  const canSubmit =
    Boolean(token) &&
    isPersonalityValid &&
    areAllAnswersFilled &&
    isCvValid &&
    !loading &&
    !cvUploading;

  const completedCount =
    (isCvValid ? 1 : 0) +
    (isPersonalityValid ? 1 : 0) +
    answers.filter((a) => a.trim().length > 0).length;

  const totalCount = 1 + 1 + QUESTIONS.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const extractCvUrl = (res) => {
    return (
      res?.data?.url ||
      res?.data?.data?.url ||
      res?.data?.fileUrl ||
      res?.data?.data?.fileUrl ||
      res?.url ||
      res?.fileUrl
    );
  };

  const runUploadCv = async (file) => {
    const reqId = ++uploadRequestIdRef.current;

    setCvUploading(true);
    setCvError("");
    setCvInfo("CV wordt geüpload...");
    setError("");

    try {
      const res = await uploadCandidateCv(file);
      const url = extractCvUrl(res);

      if (uploadRequestIdRef.current !== reqId) return;

      if (!url) {
        setCvUrl("");
        setCvError("Upload is gelukt, maar er is geen URL teruggekomen.");
        setCvInfo("");
        return;
      }

      setCvUrl(url);
      setCvInfo("CV is geüpload.");
    } catch (err) {
      if (uploadRequestIdRef.current !== reqId) return;
      setCvUrl("");
      setCvError("CV uploaden is mislukt. Probeer het opnieuw.");
      setCvInfo("");
    } finally {
      if (uploadRequestIdRef.current === reqId) setCvUploading(false);
    }
  };

  useEffect(() => {
    if (!cvFile) return;

    if (cvFile.size > MAX_CV_SIZE_BYTES) {
      setCvUrl("");
      setCvError(
        `Bestand is te groot. Maximaal ${formatBytes(MAX_CV_SIZE_BYTES)}.`
      );
      setCvInfo("");
      return;
    }

    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const isImage = cvFile.type && cvFile.type.startsWith("image/");
    const isAllowed = allowed.includes(cvFile.type) || isImage;

    if (!isAllowed) {
      setCvUrl("");
      setCvError("Onjuist bestandstype. Upload een PDF, Word of afbeelding.");
      setCvInfo("");
      return;
    }

    runUploadCv(cvFile);
  }, [cvFile]);

  const handleSubmit = async () => {
    if (!token) return;

    if (!cvUrl) {
      setCvError("Upload je CV voordat je de intake verstuurt.");
      return;
    }

    if (!canSubmit) return;

    setLoading(true);
    setError("");
    setSubmissionStatus(null);

    try {
      const payload = {
        token,
        personalityType: personalityType.trim(),
        questionnaireText,
        cvUrl: String(cvUrl).trim(),
      };

      const res = await submitCandidateIntake(payload);

      const statusFromRes = getStatusCode(res);
      if (statusFromRes === 409) {
        setSubmissionStatus("already-submitted");
        return;
      }

      setSubmissionStatus("success");
    } catch (err) {
      const status = getStatusCode(err);

      if (status === 409) {
        setError("");
        setSubmissionStatus("already-submitted");
        return;
      }

      setError("Er is iets misgegaan. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (submissionStatus === "already-submitted") {
    return (
      <PageShell>
        <GlassCard sx={{ p: { xs: 3, md: 5 }, maxWidth: 900, mx: "auto" }}>
          <Typography variant="h4" fontWeight={900}>
            Deze intake is al ingediend
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            Deze intake is eerder ontvangen. Je kunt dit formulier niet opnieuw
            indienen.
          </Typography>
          <Alert severity="info" sx={{ mt: 3 }}>
            Als je extra info wilt delen, neem contact op via de eerdere
            communicatie.
          </Alert>
        </GlassCard>
      </PageShell>
    );
  }

  if (submissionStatus === "success") {
    return (
      <PageShell>
        <GlassCard sx={{ p: { xs: 3, md: 5 }, maxWidth: 900, mx: "auto" }}>
          <Typography variant="h4" fontWeight={900}>
            Intake succesvol ontvangen
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            Dankjewel. Ons team bekijkt je antwoorden en neemt contact op over
            de volgende stap.
          </Typography>
          <Alert severity="success" sx={{ mt: 3 }}>
            Je inzending is geregistreerd.
          </Alert>
          <Box sx={{ mt: 4 }}>
            <Typography color="text.secondary">
              People First. Always.
            </Typography>
          </Box>
        </GlassCard>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Stack spacing={3} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "flex-end" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h3"
                fontWeight={950}
                sx={{ letterSpacing: -0.6 }}
              >
                Sollicitatie intake
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Beantwoord alles zo eerlijk en volledig mogelijk. CV is
                verplicht.
              </Typography>
            </Box>
          </Stack>

          {(error || cvError) && (
            <Alert severity="error">{cvError || error}</Alert>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* Left: progress */}
          <GlassCard
            sx={{
              p: 2.5,
              width: { xs: "100%", md: 360 },
              position: { md: "sticky" },
              top: { md: 24 },
            }}
          >
            <Typography fontWeight={950} sx={{ mb: 1 }}>
              Voortgang
            </Typography>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography
                variant="h3"
                fontWeight={950}
                sx={{ color: BRAND_ORANGE, lineHeight: 1 }}
              >
                {progressPct}%
              </Typography>
              <Typography color="text.secondary">
                {completedCount} van {totalCount}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                mt: 2,
                height: 10,
                borderRadius: 999,
                backgroundColor: "rgba(2,6,23,0.08)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor: BRAND_ORANGE,
                },
              }}
            />

            <Box sx={{ mt: 2.5 }}>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">CV</Typography>
                  <Typography
                    fontWeight={900}
                    sx={{
                      color: isCvValid ? BRAND_ORANGE : "rgba(2,6,23,0.6)",
                    }}
                  >
                    {isCvValid ? "OK" : "Nog nodig"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    Persoonlijkheidstype
                  </Typography>
                  <Typography
                    fontWeight={900}
                    sx={{
                      color: isPersonalityValid
                        ? BRAND_ORANGE
                        : "rgba(2,6,23,0.6)",
                    }}
                  >
                    {isPersonalityValid ? "OK" : "Nog nodig"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">Vragen</Typography>
                  <Typography fontWeight={900}>
                    {answers.filter((a) => a.trim().length > 0).length}/
                    {QUESTIONS.length}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                mt: 3,
                py: 1.4,
                borderRadius: 3,
                fontWeight: 950,
                backgroundColor: BRAND_ORANGE,
                color: "#ffffff",
                "&:hover": { backgroundColor: "#d94f1d" },
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Versturen"
              )}
            </Button>

            {!canSubmit && (
              <Typography
                color="text.secondary"
                sx={{ mt: 1.5, fontWeight: 700 }}
              >
                Vul alles in om te kunnen versturen.
              </Typography>
            )}
          </GlassCard>

          {/* Right: form */}
          <Stack spacing={3} sx={{ flex: 1, width: "100%" }}>
            {/* CV */}
            <GlassCard sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={950}>CV uploaden</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Max {formatBytes(MAX_CV_SIZE_BYTES)}. PDF, Word of
                    afbeelding.
                  </Typography>
                </Box>

                <Chip
                  label={cvUrl ? "Geüpload" : "Verplicht"}
                  sx={{
                    fontWeight: 900,
                    backgroundColor: BRAND_ORANGE,
                    color: "#ffffff",
                  }}
                />
              </Stack>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    disabled={cvUploading || loading || !token}
                    sx={{
                      borderRadius: 3,
                      fontWeight: 950,
                      borderColor: "rgba(2,6,23,0.18)",
                      color: "rgba(2,6,23,0.85)",
                      backgroundColor: "#ffffff",
                      "&:hover": { backgroundColor: "#f7f7fb" },
                    }}
                  >
                    Kies bestand
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setCvFile(file);
                        setCvUrl("");
                        setCvError("");
                        setCvInfo("");
                      }}
                    />
                  </Button>

                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={900}>
                      {cvFile ? cvFile.name : "Nog geen bestand geselecteerd"}
                    </Typography>
                    <Typography color="text.secondary">
                      {cvFile
                        ? `Grootte ${formatBytes(cvFile.size)}`
                        : "Upload start automatisch na selectie"}
                    </Typography>
                  </Box>

                  {cvUploading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography color="text.secondary" fontWeight={800}>
                        Uploaden
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {cvUploading && <LinearProgress />}

                {cvInfo && !cvError && (
                  <Alert severity="success">{cvInfo}</Alert>
                )}
              </Stack>
            </GlassCard>

            {/* Personality */}
            <GlassCard sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography fontWeight={950}>Persoonlijkheidstype</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Vul exact 4 letters in, bijvoorbeeld ENFJ
              </Typography>

              <TextField
                required
                value={personalityType}
                onChange={(e) =>
                  setPersonalityType(e.target.value.toUpperCase())
                }
                placeholder="ENFJ"
                inputProps={{ maxLength: 4 }}
                error={personalityType.length > 0 && !isPersonalityValid}
                helperText={
                  personalityType.length > 0 && !isPersonalityValid
                    ? "Vul exact 4 letters in"
                    : " "
                }
                sx={{
                  mt: 2,
                  width: { xs: "100%", sm: 280 },
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                }}
              />
            </GlassCard>

            {/* Questions */}
            <Stack spacing={2}>
              {QUESTIONS.map((question, index) => {
                const v = answers[index] || "";
                const isEmpty = v.trim().length === 0;

                return (
                  <GlassCard key={index} sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 999,
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 950,
                          color: "#ffffff",
                          backgroundColor: BRAND_ORANGE,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography fontWeight={950} sx={{ flex: 1 }}>
                        {question}
                      </Typography>
                    </Stack>

                    <TextField
                      required
                      multiline
                      minRows={4}
                      fullWidth
                      value={v}
                      onChange={(e) =>
                        handleAnswerChange(index, e.target.value)
                      }
                      placeholder="Typ hier je antwoord..."
                      error={isEmpty}
                      helperText={isEmpty ? "Dit veld is verplicht" : " "}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          backgroundColor: "#ffffff",
                        },
                      }}
                    />
                  </GlassCard>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </PageShell>
  );
};

export default CandidateIntake;
